import { useEffect, useMemo, useState } from 'react';
import { Card, Empty, List, Space, Tag, Typography, Button, Spin } from 'antd';
import { AimOutlined, CarOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  TripMapView,
  fleetPositionsToMapMarkers,
  type FleetVehiclePosition,
  type FleetMapDisplayStatus,
} from '@ubus/shared';

const { Text } = Typography;

const SYDNEY = { lat: -33.8688, lng: 151.2093 };

const STATUS_COLOR: Record<FleetMapDisplayStatus, string> = {
  available: 'success',
  on_trip: 'processing',
  pending: 'warning',
  maintenance: 'default',
  stale: 'orange',
  unknown: 'default',
};

type FleetMapPanelProps = {
  positions: FleetVehiclePosition[];
  loading?: boolean;
  title: string;
  subtitle?: string;
};

export function FleetMapPanel({ positions, loading, title, subtitle }: FleetMapPanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);

  const withPoint = useMemo(
    () => positions.filter((p) => p.lat != null && p.lng != null),
    [positions]
  );
  const withoutPoint = useMemo(
    () => positions.filter((p) => p.lat == null || p.lng == null),
    [positions]
  );

  const markers = useMemo(() => fleetPositionsToMapMarkers(positions), [positions]);

  const center = useMemo(() => {
    if (withPoint.length === 0) return SYDNEY;
    const lat = withPoint.reduce((s, p) => s + (p.lat as number), 0) / withPoint.length;
    const lng = withPoint.reduce((s, p) => s + (p.lng as number), 0) / withPoint.length;
    return { lat, lng };
  }, [withPoint]);

  const selected = selectedId
    ? positions.find((p) => p.vehicleId === selectedId) ?? null
    : null;

  useEffect(() => {
    if (selectedId && !positions.some((p) => p.vehicleId === selectedId)) {
      setSelectedId(null);
    }
  }, [positions, selectedId]);

  return (
    <Card
      title={
        <Space>
          <AimOutlined />
          <span>{title}</span>
          <Tag>{withPoint.length}/{positions.length}</Tag>
        </Space>
      }
      extra={subtitle ? <Text type="secondary">{subtitle}</Text> : null}
      styles={{ body: { padding: 12 } }}
    >
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 480px', minWidth: 280, minHeight: 360, position: 'relative' }}>
          {loading ? (
            <div style={{ display: 'grid', placeItems: 'center', height: 360 }}>
              <Spin />
            </div>
          ) : mapError ? (
            <Empty description={mapError} />
          ) : withPoint.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('dashboard.fleetMapEmpty')}
              style={{ paddingTop: 80 }}
            />
          ) : (
            <TripMapView
              center={center}
              zoom={11}
              markers={markers}
              style={{ height: 360, width: '100%', borderRadius: 8 }}
              onError={(msg) => setMapError(msg)}
            />
          )}
          <Space size={[4, 4]} wrap style={{ marginTop: 8 }}>
            {(Object.keys(STATUS_COLOR) as FleetMapDisplayStatus[]).map((key) => (
              <Tag key={key} color={STATUS_COLOR[key]}>
                {t(`dashboard.mapStatus.${key}`)}
              </Tag>
            ))}
          </Space>
        </div>

        <div style={{ flex: '0 1 280px', minWidth: 240, maxHeight: 400, overflow: 'auto' }}>
          {selected ? (
            <Card size="small" title={selected.fleetNumber} style={{ marginBottom: 8 }}>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Tag color={STATUS_COLOR[selected.displayStatus]}>
                  {t(`dashboard.mapStatus.${selected.displayStatus}`)}
                </Tag>
                <Text type="secondary">
                  {selected.registrationPlate || t('vehicles.plateMissing')}
                </Text>
                <Text type="secondary">
                  {selected.lastSeenAt
                    ? t('dashboard.lastSeen', { time: new Date(selected.lastSeenAt).toLocaleString() })
                    : t('dashboard.noGps')}
                </Text>
                <Space wrap>
                  <Button size="small" onClick={() => navigate(`/vehicles`)}>
                    {t('dashboard.openVehicle')}
                  </Button>
                  {selected.tripId ? (
                    <Button size="small" type="primary" onClick={() => navigate(`/trips/${selected.tripId}/map`)}>
                      {t('dashboard.openTripMap')}
                    </Button>
                  ) : null}
                </Space>
              </Space>
            </Card>
          ) : null}

          <List
            size="small"
            header={
              <Text strong>
                <CarOutlined /> {t('dashboard.fleetVehicles')}
              </Text>
            }
            dataSource={positions}
            locale={{ emptyText: t('dashboard.noVehicles') }}
            renderItem={(item) => (
              <List.Item
                style={{
                  cursor: item.lat != null ? 'pointer' : 'default',
                  background: item.vehicleId === selectedId ? 'rgba(37,99,235,0.06)' : undefined,
                  paddingInline: 8,
                }}
                onClick={() => {
                  if (item.lat != null) setSelectedId(item.vehicleId);
                }}
              >
                <Space direction="vertical" size={0} style={{ width: '100%' }}>
                  <Space wrap>
                    <Text strong>{item.fleetNumber}</Text>
                    <Tag color={STATUS_COLOR[item.displayStatus]}>
                      {t(`dashboard.mapStatus.${item.displayStatus}`)}
                    </Tag>
                    {item.online ? <Tag color="green">{t('dashboard.onlineTag')}</Tag> : null}
                  </Space>
                  {item.lat == null ? (
                    <Text type="secondary">{t('dashboard.noGps')}</Text>
                  ) : null}
                </Space>
              </List.Item>
            )}
          />

          {withoutPoint.length > 0 ? (
            <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              {t('dashboard.noLocationCount', { count: withoutPoint.length })}
            </Text>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
